import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { formatMetric } from './format';
import { isMissing } from './metrics';
import { LegendToggle, useHiddenSet, TREND_COLORS } from './charts';

const EARLIER_COLOR = '#94a3b8';
const CURRENT_COLOR = '#6f020d';

function currentColor(verdict) {
  if (verdict === 'above') return TREND_COLORS.worse;
  if (verdict === 'below') return TREND_COLORS.better;
  return CURRENT_COLOR;
}

// Earlier releases, oldest first, then this release.
function buildReleaseTrend(points, range, kpi, verdict) {
  const last = points.length - 1;
  const colors = points.map((point, index) => (index === last ? currentColor(verdict) : EARLIER_COLOR));
  const band = (bound) => points.map(() => (isMissing(range?.low) ? null : range[bound]));
  return {
    labels: points.map((point) => point.label),
    datasets: [
      {
        label: 'Usual range of previous releases (high)', legendKey: 'band', data: band('high'), band: true, borderWidth: 0, pointRadius: 0, fill: '+1', backgroundColor: TREND_COLORS.band, unit: kpi.unit,
      },
      {
        label: 'Usual range of previous releases (low)', legendKey: 'band', data: band('low'), band: true, borderWidth: 0, pointRadius: 0, fill: false, unit: kpi.unit,
      },
      {
        label: kpi.title,
        legendKey: 'cost',
        data: points.map((point) => point.value),
        borderColor: EARLIER_COLOR,
        backgroundColor: EARLIER_COLOR,
        borderWidth: 2,
        tension: 0.25,
        pointRadius: points.map((point, index) => (index === last ? 6 : 4)),
        pointBackgroundColor: colors,
        pointBorderColor: colors,
        spanGaps: true,
        unit: kpi.unit,
        signed: kpi.signed,
      },
    ],
  };
}

// `points`: [{ label, value }], oldest first, this release last.
export function ReleaseTrend({ points, range, kpis, kpi, onSelect, verdict }) {
  const data = useMemo(() => buildReleaseTrend(points, range, kpi, verdict), [points, range, kpi, verdict]);
  const [hidden, toggle] = useHiddenSet();
  const shownData = { ...data, datasets: data.datasets.map((dataset) => ({ ...dataset, hidden: hidden.has(dataset.legendKey) })) };
  const hasData = points.some((point) => !isMissing(point.value));
  return (
    <div className="sdkp-panel sdkp-section-spaced" data-panel="release-trend">
      <div className="sdkp-panel-head">
        <h2 className="sdkp-panel-title">This release against up to 10 previous releases</h2>
        <div className="sdkp-segmented" aria-label="Trend metric">
          {kpis.map((item) => (
            <button key={item.key} type="button" className={item.key === kpi.key ? 'sdkp-active' : ''} onClick={() => onSelect(item.key)}>
              {item.tab}
            </button>
          ))}
        </div>
      </div>
      <p className="sdkp-panel-hint">One point per earlier release, then this release. Shaded band = usual range of the previous releases.</p>
      {!hasData && <div className="sdkp-empty">No {kpi.tab} data for the selected filters.</div>}
      {hasData && (
        <div className="sdkp-chart">
          <Line
            data={shownData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { display: false },
                tooltip: {
                  filter: (item) => !item.dataset.band,
                  callbacks: {
                    label: (context) => `${context.dataset.label}: ${formatMetric(context.parsed.y, context.dataset.unit, context.dataset.signed)}`,
                  },
                },
              },
              scales: {
                x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
                y: {
                  beginAtZero: true,
                  title: { display: true, text: kpi.axisTitle, font: { size: 11, weight: 'bold' } },
                },
              },
            }}
          />
        </div>
      )}
      <div className="sdkp-legend">
        <span><span className="sdkp-swatch" style={{ background: EARLIER_COLOR }} />Previous releases</span>
        <span><span className="sdkp-swatch" style={{ background: CURRENT_COLOR }} />This release</span>
        <LegendToggle hidden={hidden.has('band')} onToggle={() => toggle('band')} swatchStyle={{ background: TREND_COLORS.band }}>Usual range of previous releases</LegendToggle>
      </div>
    </div>
  );
}
